import { Component } from '@theme/component';
import { QuoteList, QuoteUpdateEvent } from '@theme/quote-list';

/**
 * Renders the quote list inside the request-quote contact form, with quantity controls,
 * and keeps a hidden field populated with a plain-text summary of the items so the
 * native Shopify contact form emails them to the store.
 *
 * @typedef {object} Refs
 * @property {HTMLTextAreaElement} quoteBody - Hidden field submitted with the contact form.
 *
 * @extends {Component<Refs>}
 */
class QuoteFormItems extends Component {
  requiredRefs = ['quoteBody'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(QuoteUpdateEvent.eventName, this.#render);
    this.addEventListener('click', this.#handleItemAction);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(QuoteUpdateEvent.eventName, this.#render);
    this.removeEventListener('click', this.#handleItemAction);
  }

  /**
   * Delegated handler for +/- and remove buttons.
   * @param {Event} event
   */
  #handleItemAction = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-quote-action]') : null;
    if (!target) return;

    event.preventDefault();
    const id = target.getAttribute('data-quote-id');
    if (!id) return;

    const action = target.getAttribute('data-quote-action');
    const current = QuoteList.items().find((item) => String(item.id) === String(id));
    const quantity = current ? Number(current.quantity) || 1 : 0;

    if (action === 'increment') QuoteList.updateQuantity(id, quantity + 1);
    else if (action === 'decrement') QuoteList.updateQuantity(id, quantity - 1);
    else if (action === 'remove') QuoteList.remove(id);
  };

  #render = () => {
    const list = this.querySelector('[data-quote-form-items]');
    const emptyState = this.querySelector('[data-quote-form-empty]');
    const items = QuoteList.items();
    const hasItems = items.length > 0;

    if (emptyState) emptyState.classList.toggle('hidden', hasItems);
    if (list) list.innerHTML = hasItems ? items.map((item) => this.#renderItem(item)).join('') : '';

    // Keep the hidden field in sync so it is submitted with the contact form.
    this.refs.quoteBody.value = hasItems ? this.#buildSummary(items) : '';
  };

  /**
   * @param {import('@theme/quote-list').QuoteItem[]} items
   * @returns {string}
   */
  #buildSummary(items) {
    return items
      .map((item, index) => {
        const variant = item.variantTitle ? ` — ${item.variantTitle}` : '';
        const sku = item.sku ? ` (SKU: ${item.sku})` : '';
        const url = item.url ? ` — ${this.#absoluteUrl(item.url)}` : '';
        return `${index + 1}) ${item.quantity}x ${item.title}${variant}${sku}${url}`;
      })
      .join('\n');
  }

  /** @param {string} url */
  #absoluteUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (_) {
      return url;
    }
  }

  /**
   * @param {import('@theme/quote-list').QuoteItem} item
   * @returns {string}
   */
  #renderItem(item) {
    const image = item.image
      ? `<img class="quote-form__item-image" src="${escapeAttr(item.image)}" alt="" width="56" height="56" loading="lazy">`
      : '<span class="quote-form__item-image quote-form__item-image--empty"></span>';
    const variant = item.variantTitle ? `<div class="quote-form__item-variant">${escapeHtml(item.variantTitle)}</div>` : '';
    const sku = item.sku ? `<div class="quote-form__item-sku">SKU: ${escapeHtml(item.sku)}</div>` : '';
    const id = escapeAttr(String(item.id));
    const quantity = Number(item.quantity) || 1;

    return `
      <li class="quote-form__item">
        <a href="${escapeAttr(item.url || '#')}" class="quote-form__item-media">${image}</a>
        <div class="quote-form__item-info">
          <a href="${escapeAttr(item.url || '#')}" class="quote-form__item-title">${escapeHtml(item.title || '')}</a>
          ${variant}
          ${sku}
        </div>
        <div class="quote-form__item-controls">
          <div class="quote-form__qty">
            <button type="button" class="quote-form__qty-btn" data-quote-action="decrement" data-quote-id="${id}" aria-label="Decrease quantity">&minus;</button>
            <span class="quote-form__qty-value">${quantity}</span>
            <button type="button" class="quote-form__qty-btn" data-quote-action="increment" data-quote-id="${id}" aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="quote-form__remove button-unstyled" data-quote-action="remove" data-quote-id="${id}">Remove</button>
        </div>
      </li>
    `;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char;
  });
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('quote-form-items')) {
  customElements.define('quote-form-items', QuoteFormItems);
}
